/**
 * Market Discovery Cache Service
 *
 * Implements multi-layer caching strategy for market discovery data:
 * - In-memory cache with TTL
 * - localStorage persistence for cross-session
 * - Stale-while-revalidate pattern
 */

import type { FuturesSpotSymbol } from '../types/futuresSpot';

/**
 * Cache configuration
 */
interface CacheConfig {
  /** Time To Live in milliseconds (default: 5 minutes) */
  ttl: number;
  /** localStorage key prefix */
  storageKey: string;
  /** Schema version for cache invalidation on structure changes */
  version: number;
}

const DEFAULT_CONFIG: CacheConfig = {
  ttl: 5 * 60 * 1000, // 5 minutes
  storageKey: 'futuresSpotMarkets',
  version: 1,
};

/**
 * Cache entry structure
 */
interface CacheEntry {
  data: FuturesSpotSymbol[];
  timestamp: number;
  version: number;
}

/**
 * Market Discovery Cache Service
 */
export class MarketDiscoveryCache {
  private config: CacheConfig;
  private memoryCache: CacheEntry | null = null;

  // Metrics
  private hits: number = 0;
  private misses: number = 0;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Get cached data if available and fresh
   *
   * @returns Cached data or null if cache miss/stale
   */
  get(): FuturesSpotSymbol[] | null {
    // Try memory cache first
    const memoryCached = this.getFromMemory();
    if (memoryCached) {
      this.hits++;
      console.debug('[MarketDiscoveryCache] Memory cache HIT');
      return memoryCached;
    }

    // Try localStorage
    const storageCached = this.getFromStorage();
    if (storageCached) {
      // Restore to memory cache
      this.memoryCache = {
        data: storageCached,
        timestamp: Date.now(),
        version: this.config.version,
      };

      this.hits++;
      console.debug('[MarketDiscoveryCache] localStorage cache HIT (restored to memory)');
      return storageCached;
    }

    // Cache miss
    this.misses++;
    console.debug('[MarketDiscoveryCache] Cache MISS');
    return null;
  }

  /**
   * Store data in cache
   *
   * @param data - Market discovery data to cache
   */
  set(data: FuturesSpotSymbol[]): void {
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      version: this.config.version,
    };

    // Store in memory
    this.memoryCache = entry;

    // Persist to localStorage
    try {
      const serialized = JSON.stringify(entry);
      localStorage.setItem(this.getCacheKey(), serialized);
      console.debug(`[MarketDiscoveryCache] Cached ${data.length} symbols (memory + localStorage)`);
    } catch (error) {
      console.warn('[MarketDiscoveryCache] Failed to persist to localStorage:', error);
      // Continue with memory-only cache
    }
  }

  /**
   * Invalidate cache manually
   */
  invalidate(): void {
    this.memoryCache = null;

    try {
      localStorage.removeItem(this.getCacheKey());
      console.info('[MarketDiscoveryCache] Cache invalidated');
    } catch (error) {
      console.warn('[MarketDiscoveryCache] Failed to clear localStorage:', error);
    }
  }

  /**
   * Get cache metrics
   */
  getMetrics(): { hits: number; misses: number; hitRate: number } {
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? (this.hits / total) * 100 : 0;

    return {
      hits: this.hits,
      misses: this.misses,
      hitRate: parseFloat(hitRate.toFixed(2)),
    };
  }

  /**
   * Check if cache is stale
   */
  isStale(): boolean {
    if (!this.memoryCache) {
      return true;
    }

    const age = Date.now() - this.memoryCache.timestamp;
    return age > this.config.ttl;
  }

  /**
   * Get remaining TTL in milliseconds
   */
  getRemainingTTL(): number {
    if (!this.memoryCache) {
      return 0;
    }

    const age = Date.now() - this.memoryCache.timestamp;
    const remaining = this.config.ttl - age;
    return Math.max(0, remaining);
  }

  // Private methods

  private getFromMemory(): FuturesSpotSymbol[] | null {
    if (!this.memoryCache) {
      return null;
    }

    // Check version
    if (this.memoryCache.version !== this.config.version) {
      console.info('[MarketDiscoveryCache] Version mismatch, invalidating memory cache');
      this.memoryCache = null;
      return null;
    }

    // Check TTL
    const age = Date.now() - this.memoryCache.timestamp;
    if (age > this.config.ttl) {
      console.debug('[MarketDiscoveryCache] Memory cache expired');
      this.memoryCache = null;
      return null;
    }

    return this.memoryCache.data;
  }

  private getFromStorage(): FuturesSpotSymbol[] | null {
    try {
      const cached = localStorage.getItem(this.getCacheKey());
      if (!cached) {
        return null;
      }

      const entry: CacheEntry = JSON.parse(cached);

      // Check version
      if (entry.version !== this.config.version) {
        console.info('[MarketDiscoveryCache] Version mismatch in localStorage, invalidating');
        localStorage.removeItem(this.getCacheKey());
        return null;
      }

      // Check TTL
      const age = Date.now() - entry.timestamp;
      if (age > this.config.ttl) {
        console.debug('[MarketDiscoveryCache] localStorage cache expired');
        localStorage.removeItem(this.getCacheKey());
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn('[MarketDiscoveryCache] Failed to read from localStorage:', error);
      return null;
    }
  }

  private getCacheKey(): string {
    return `${this.config.storageKey}:v${this.config.version}`;
  }
}

/**
 * Stale-While-Revalidate Wrapper
 *
 * Provides stale-while-revalidate pattern:
 * - Returns stale data immediately if available
 * - Fetches fresh data in background
 * - Updates cache when fresh data arrives
 */
export class StaleWhileRevalidate<T> {
  private fetchFn: () => Promise<T>;
  private cache: MarketDiscoveryCache;
  private revalidating: boolean = false;

  constructor(fetchFn: () => Promise<T>, cache: MarketDiscoveryCache) {
    this.fetchFn = fetchFn;
    this.cache = cache;
  }

  /**
   * Get data with stale-while-revalidate pattern
   *
   * @returns { data, isStale } - Data and freshness indicator
   */
  async get(): Promise<{ data: T; isStale: boolean }> {
    // Try cache first
    const cached = this.cache.get() as T | null;

    if (cached) {
      const isStale = this.cache.isStale();

      // If stale but not already revalidating, trigger background refresh
      if (isStale && !this.revalidating) {
        this.revalidating = true;
        console.info('[StaleWhileRevalidate] Serving stale data, fetching fresh in background...');

        // Background revalidation (don't await)
        this.fetchFn()
          .then((freshData) => {
            this.cache.set(freshData as any);
            console.info('[StaleWhileRevalidate] Background revalidation complete');
          })
          .catch((error) => {
            console.error('[StaleWhileRevalidate] Background revalidation failed:', error);
          })
          .finally(() => {
            this.revalidating = false;
          });
      }

      return { data: cached, isStale };
    }

    // Cache miss - fetch fresh data
    console.info('[StaleWhileRevalidate] Cache miss, fetching fresh data...');
    const freshData = await this.fetchFn();
    this.cache.set(freshData as any);

    return { data: freshData, isStale: false };
  }

  /**
   * Force refresh (bypass cache)
   */
  async refresh(): Promise<T> {
    console.info('[StaleWhileRevalidate] Force refresh requested');
    const freshData = await this.fetchFn();
    this.cache.set(freshData as any);
    return freshData;
  }
}
